import { useState, useEffect, useMemo, useRef } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { usePodList } from '../../../stores/podStore';

export default function PodUserMultiSelect({ selectedUsers = [], onChange }) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const knownUsersRef = useRef(new Map());

  // Debounce user search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch POD users with search
  const { data: podUsers = [], isLoading } = usePodList(0, 100, [], 'username', debouncedSearch);

  // Cache fetched user objects so previously selected users don't vanish on new searches
  useEffect(() => {
    if (Array.isArray(podUsers)) {
      podUsers.forEach((u) => {
        if (u && u.id) {
          knownUsersRef.current.set(u.id, u);
        }
      });
    }
  }, [podUsers]);

  // Build combined options list including any selected users that might not be in current search results
  const options = useMemo(() => {
    const list = [...podUsers];
    selectedUsers.forEach((id) => {
      if (!list.some((item) => item.id === id)) {
        const cached = knownUsersRef.current.get(id);
        if (cached) {
          list.push(cached);
        } else {
          list.push({ id, username: `User #${id}` });
        }
      }
    });
    return list;
  }, [podUsers, selectedUsers]);

  // Compute selected user objects for Autocomplete value prop
  const value = useMemo(() => {
    return selectedUsers.map((id) => {
      const found = options.find((opt) => opt.id === id);
      return found || knownUsersRef.current.get(id) || { id, username: `User #${id}` };
    });
  }, [selectedUsers, options]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      loading={isLoading}
      value={value}
      onChange={(event, newValue) => {
        const ids = (newValue || []).map((item) => (typeof item === 'object' ? item.id : item)).filter(Boolean);
        onChange(ids);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        }
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        const username = option.username || option.name || `User #${option.id}`;
        const company = option.join?.company_name || option.company_name || option.company?.name;
        return company ? `${username} (${company})` : username;
      }}
      isOptionEqualToValue={(option, val) => option.id === (val?.id ?? val)}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        const username = option.username || option.name || `User #${option.id}`;
        const company = option.join?.company_name || option.company_name || option.company?.name;
        const role = option.join?.role_name || option.role_name || option.role?.name;

        return (
          <Box component="li" key={key} {...restProps}>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {username}
              </Typography>
              {(company || role) && (
                <Typography variant="caption" color="text.secondary">
                  {[company, role].filter(Boolean).join(' • ')}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const label = option.username || option.name || `User #${option.id}`;
          return <Chip key={key} label={label} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="User (POD)"
          placeholder={selectedUsers.length === 0 ? 'Search POD user...' : ''}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
}
